// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { showCreditsToast } from '@/components/Toast/creditsToast';
import { showStorageToast } from '@/components/Toast/storageToast';
import { showTrafficToast } from '@/components/Toast/trafficToast';
import { createHost } from '@/host/createHost';
import { getAuthStore } from '@/store/authStore';
import {
  getConnectionConfig,
  setConnectionConfig,
} from '@/store/connectionStore';
import {
  EventSourceMessage,
  fetchEventSource,
} from '@microsoft/fetch-event-source';
import i18next from 'i18next';
import { getDesktopInstanceId } from '@/lib/desktopIdentity';

const defaultHeaders = {
  'Content-Type': 'application/json',
};
const LOCAL_CONTROL_CAPABILITY_HEADER = 'X-Eigent-Local-Capability';
let localControlCapabilityPromise: Promise<string> | null = null;

export async function getLocalControlCapability(): Promise<string> {
  const api = createHost().electronAPI;
  if (!api?.getLocalControlCapability) {
    return '';
  }
  if (!localControlCapabilityPromise) {
    localControlCapabilityPromise = Promise.resolve(
      api.getLocalControlCapability()
    ).then(
      (token) => {
        if (!token) {
          localControlCapabilityPromise = null;
        }
        return token || '';
      },
      () => {
        localControlCapabilityPromise = null;
        return '';
      }
    );
  }
  return localControlCapabilityPromise;
}

export function getDefaultBrainEndpoint(): string {
  const envEndpoint = import.meta.env.VITE_BRAIN_ENDPOINT;
  if (envEndpoint && typeof envEndpoint === 'string') {
    return envEndpoint.replace(/\/$/, '');
  }
  if (import.meta.env.DEV) {
    return 'http://localhost:5001';
  }
  return '';
}

function persistSessionIdFromResponse(response: Response): void {
  const sessionId = response.headers.get('x-session-id');
  if (!sessionId) {
    return;
  }
  const current = getConnectionConfig().sessionId;
  if (current !== sessionId) {
    setConnectionConfig({ sessionId });
  }
}

function shouldAttachAuthHeader(url: string): boolean {
  // This runs before getBaseURL() prefixes Brain-relative paths. Relative
  // routes are our Brain calls and should carry auth; absolute URLs may point
  // at third-party targets and must not receive the user's token.
  return !url.includes('http://') && !url.includes('https://');
}

async function buildBrainHeaders(
  url: string,
  customHeaders: Record<string, string> = {},
  includeContentType = true
): Promise<Record<string, string>> {
  const { token, user_id } = getAuthStore();
  const conn = getConnectionConfig();
  const headers: Record<string, string> = {
    ...(includeContentType ? defaultHeaders : {}),
    'X-Channel': conn.channel,
    ...customHeaders,
  };
  if (conn.sessionId) {
    headers['X-Session-ID'] = conn.sessionId;
  }
  if (token && shouldAttachAuthHeader(url)) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (shouldAttachAuthHeader(url)) {
    const localControlCapability = await getLocalControlCapability();
    if (localControlCapability) {
      headers[LOCAL_CONTROL_CAPABILITY_HEADER] = localControlCapability;
    }
    const desktopInstanceId = await getDesktopInstanceId().catch(() => '');
    if (desktopInstanceId) {
      headers['X-Desktop-Instance-ID'] = desktopInstanceId;
    }
  }
  if (user_id != null) {
    headers['X-User-ID'] = String(user_id);
  }
  return headers;
}

/** Reset cached baseUrl (e.g. when backend restarts). */
export function resetBaseURL(): void {
  setConnectionConfig({ brainEndpoint: '' });
}

export async function getBaseURL() {
  const cfg = getConnectionConfig();
  if (cfg.brainEndpoint) {
    return cfg.brainEndpoint.replace(/\/$/, '');
  }
  const remoteEndpoint = await createHost().ipcRenderer?.invoke('get-backend-endpoint');
  if (typeof remoteEndpoint === 'string' && remoteEndpoint) {
    const resolved = remoteEndpoint.replace(/\/$/, '');
    setConnectionConfig({ brainEndpoint: resolved });
    return resolved;
  }
  // Electron: get port from IPC
  const port = await createHost().ipcRenderer?.invoke('get-backend-port');
  if (port && port > 0) {
    const resolved = `http://localhost:${port}`;
    setConnectionConfig({ brainEndpoint: resolved });
    return resolved;
  }
  // Pure Web: use VITE_BRAIN_ENDPOINT (dev default http://localhost:5001)
  const envEndpoint = getDefaultBrainEndpoint();
  if (envEndpoint && typeof envEndpoint === 'string') {
    const resolved = envEndpoint.replace(/\/$/, ''); // trim trailing slash
    setConnectionConfig({ brainEndpoint: resolved });
    return resolved;
  }
  return '';
}

type FetchRequestOptions = {
  signal?: AbortSignal;
};

async function fetchRequest(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  data?: Record<string, any>,
  customHeaders: Record<string, string> = {},
  requestOptions: FetchRequestOptions = {}
): Promise<any> {
  const baseURL = await getBaseURL();
  const fullUrl = `${baseURL}${url}`;
  const headers = await buildBrainHeaders(url, customHeaders);

  const options: RequestInit = {
    method,
    headers,
    signal: requestOptions.signal,
  };

  if (method === 'GET') {
    const queryParams = new URLSearchParams();
    Object.entries(data ?? {}).forEach(([key, value]) => {
      const values = Array.isArray(value) ? value : [value];
      values.forEach((item) => {
        if (item !== undefined && item !== null) {
          queryParams.append(key, String(item));
        }
      });
    });
    const query = queryParams.size > 0 ? `?${queryParams.toString()}` : '';
    return handleResponse(fetch(fullUrl + query, options), data);
  }

  if (data) {
    options.body = JSON.stringify(data);
  }

  return handleResponse(fetch(fullUrl, options), data);
}

async function handleResponse(
  responsePromise: Promise<Response>,
  requestData?: Record<string, any>
): Promise<any> {
  try {
    const res = await responsePromise;
    persistSessionIdFromResponse(res);
    if (res.status === 204) {
      return { code: 0, text: '' };
    }
    if (res.status === 304) {
      return { code: 0, not_modified: true };
    }

    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        const msg = detail?.trim() || `HTTP ${res.status}`;
        const err = new Error(msg);
        (err as any).status = res.status;
        (err as any).response = res;
        throw err;
      }
      if (res.body) {
        return {
          isStream: true,
          body: res.body,
          reader: res.body.getReader(),
        };
      }
      return null;
    }
    const resData = await res.json();
    if (!resData) {
      return null;
    }
    const { code, text } = resData;
    // showCreditsToast()
    if (code === 1 || code === 300) {
      return resData;
    }

    if (code === 20) {
      showCreditsToast();
      return resData;
    }

    if (code === 21) {
      showStorageToast();
      return resData;
    }

    if (code === 13) {
      // const { logout } = getAuthStore()
      // logout()
      // window.location.href = '#/login'
      throw new Error(text);
    }

    if (!res.ok) {
      const detail = resData?.detail;
      const detailMessage = Array.isArray(detail)
        ? detail[0]
        : typeof detail === 'string'
          ? detail
          : null;
      const objectMessage =
        detail && typeof detail === 'object'
          ? detail.message || detail.code || JSON.stringify(detail)
          : null;
      const msg =
        detailMessage ||
        objectMessage ||
        resData?.message ||
        `HTTP ${res.status}`;
      const err: any = new Error(
        typeof msg === 'string' ? msg : JSON.stringify(msg)
      );
      err.status = res.status;
      err.response = { data: resData, status: res.status };
      throw err;
    }

    return resData;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw err;
    }

    // Only show traffic toast for cloud model requests
    const isCloudRequest = requestData?.api_url === 'cloud';
    if (isCloudRequest) {
      showTrafficToast();
    }

    console.error('[fetch error]:', err);

    if (err?.response?.status === 401) {
      // const { logout } = getAuthStore()
      // logout()
      // window.location.href = '#/login'
    }

    throw err;
  }
}

// Encapsulate common methods
export const fetchGet = (
  url: string,
  params?: any,
  headers?: any,
  options?: FetchRequestOptions
) => fetchRequest('GET', url, params, headers, options);

/** GET a bounded binary payload from Brain without converting it to a stream. */
export async function fetchGetBlob(
  url: string,
  params?: Record<string, unknown>,
  options: FetchRequestOptions = {}
): Promise<Blob> {
  const baseURL = await getBaseURL();
  const queryParams = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];
    values.forEach((item) => {
      if (item !== undefined && item !== null) {
        queryParams.append(key, String(item));
      }
    });
  });
  const query = queryParams.size > 0 ? `?${queryParams.toString()}` : '';
  const response = await fetch(`${baseURL}${url}${query}`, {
    method: 'GET',
    headers: await buildBrainHeaders(url, { Accept: '*/*' }, false),
    signal: options.signal,
  });
  persistSessionIdFromResponse(response);
  if (!response.ok) {
    const contentType = response.headers.get('content-type') || '';
    let message = `HTTP ${response.status}`;
    if (contentType.includes('application/json')) {
      const body = await response.json().catch(() => null);
      const detail = body?.detail;
      message =
        (typeof detail === 'string' && detail) || body?.message || message;
    } else {
      const detail = await response.text().catch(() => '');
      if (detail.trim()) message = detail.trim();
    }
    const error = new Error(message) as Error & {
      status?: number;
      response?: Response;
    };
    error.status = response.status;
    error.response = response;
    throw error;
  }
  return response.blob();
}

export const fetchPost = (url: string, data?: any, headers?: any) =>
  fetchRequest('POST', url, data, headers);

export const fetchPut = (url: string, data?: any, headers?: any) =>
  fetchRequest('PUT', url, data, headers);

export const fetchPatch = (url: string, data?: any, headers?: any) =>
  fetchRequest('PATCH', url, data, headers);

export const fetchDelete = (
  url: string,
  data?: any,
  headers?: any,
  options?: FetchRequestOptions
) => fetchRequest('DELETE', url, data, headers, options);

/** POST FormData to Brain base URL (for file uploads). */
export async function fetchPostForm(
  url: string,
  formData: FormData,
  customHeaders: Record<string, string> = {}
): Promise<any> {
  const baseURL = await getBaseURL();
  const fullUrl = `${baseURL}${url}`;
  const headers = await buildBrainHeaders(url, customHeaders, false);
  return handleResponse(
    fetch(fullUrl, { method: 'POST', headers, body: formData })
  );
}

export async function uploadFileToBrain(file: globalThis.File): Promise<{
  file_id: string;
  filename: string;
  size: number;
}> {
  const formData = new FormData();
  formData.append('file', file);
  return fetchPostForm('/files', formData);
}

export interface SSETransportOptions {
  url: string;
  method?: 'GET' | 'POST';
  body?: Record<string, any> | string;
  signal?: AbortSignal;
  extraHeaders?: Record<string, string>;
  openWhenHidden?: boolean;
  onmessage: (event: EventSourceMessage) => void | Promise<void>;
  onopen?: (response: Response) => void | Promise<void>;
  onerror?: (err: any) => number | null | undefined | void;
  onclose?: () => void;
}

export async function sseTransport(
  options: SSETransportOptions
): Promise<void> {
  const baseURL = await getBaseURL();
  const fullUrl =
    options.url.startsWith('http://') || options.url.startsWith('https://')
      ? options.url
      : `${baseURL}${options.url}`;

  const headers = await buildBrainHeaders(options.url, options.extraHeaders);
  const body =
    typeof options.body === 'string'
      ? options.body
      : options.body
        ? JSON.stringify(options.body)
        : undefined;

  await fetchEventSource(fullUrl, {
    method: options.method || 'POST',
    openWhenHidden: options.openWhenHidden ?? true,
    signal: options.signal,
    headers,
    body,
    onmessage: options.onmessage,
    async onopen(response) {
      persistSessionIdFromResponse(response);
      if (options.onopen) {
        await options.onopen(response);
      }
    },
    onerror: options.onerror,
    onclose: options.onclose,
  });
}

// =============== porxy ===============

// get proxy base URL
export async function getProxyBaseURL() {
  const isDev = import.meta.env.DEV;

  if (isDev) {
    // Use empty base so request goes to same origin; Vite proxy forwards /api to VITE_PROXY_URL
    // This avoids CORS when running dev:web (browser at 5173, server at 3001)
    return '';
  } else {
    const useLocalProxy = import.meta.env.VITE_USE_LOCAL_PROXY === 'true';
    const proxyUrl = import.meta.env.VITE_PROXY_URL;
    const baseUrl =
      !useLocalProxy && proxyUrl
        ? proxyUrl
        : import.meta.env.VITE_BASE_URL || proxyUrl;
    if (baseUrl) {
      return String(baseUrl).replace(/\/$/, '');
    }

    const backendEndpoint = await getBaseURL();
    if (!backendEndpoint) {
      throw new Error('Keystone backend endpoint is not configured');
    }
    return backendEndpoint.replace(/\/$/, '');
  }
}

async function proxyFetchRequest(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  data?: Record<string, any>,
  customHeaders: Record<string, string> = {}
): Promise<any> {
  const baseURL = await getProxyBaseURL();
  const fullUrl = `${baseURL}${url}`;
  const { token } = getAuthStore();

  const headers: Record<string, string> = {
    ...defaultHeaders,
    ...customHeaders,
  };

  if (!url.includes('http://') && !url.includes('https://') && token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (import.meta.env.DEV) {
    const targetUrl = import.meta.env.VITE_BASE_URL;
    if (targetUrl) {
      headers['X-Proxy-Target'] = targetUrl;
    }
  }

  const options: RequestInit = {
    method,
    headers,
  };

  if (method === 'GET') {
    const query = data
      ? '?' +
        Object.entries(data)
          .map(
            ([key, val]) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
          )
          .join('&')
      : '';
    return handleResponse(fetch(fullUrl + query, options));
  }

  if (data) {
    options.body = JSON.stringify(data);
  }

  return handleResponse(fetch(fullUrl, options));
}

export const proxyFetchGet = (url: string, params?: any, headers?: any) =>
  proxyFetchRequest('GET', url, params, headers);

export const proxyFetchPost = (url: string, data?: any, headers?: any) =>
  proxyFetchRequest('POST', url, data, headers);

export const proxyFetchPut = (url: string, data?: any, headers?: any) =>
  proxyFetchRequest('PUT', url, data, headers);

export const proxyFetchPatch = (url: string, data?: any, headers?: any) =>
  proxyFetchRequest('PATCH', url, data, headers);

export const proxyFetchDelete = (url: string, data?: any, headers?: any) =>
  proxyFetchRequest('DELETE', url, data, headers);

// File upload function with FormData
export async function uploadFile(
  url: string,
  formData: FormData,
  headers?: Record<string, string>
): Promise<any> {
  const baseURL = await getProxyBaseURL();
  const fullUrl = `${baseURL}${url}`;
  const { token } = getAuthStore();

  const requestHeaders: Record<string, string> = {
    ...headers,
  };

  // Remove Content-Type header to let browser set it with boundary for FormData
  if (requestHeaders['Content-Type']) {
    delete requestHeaders['Content-Type'];
  }

  if (!url.includes('http://') && !url.includes('https://') && token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  if (import.meta.env.DEV) {
    const targetUrl = import.meta.env.VITE_BASE_URL;
    if (targetUrl) {
      requestHeaders['X-Proxy-Target'] = targetUrl;
    }
  }

  const options: RequestInit = {
    method: 'POST',
    headers: requestHeaders,
    body: formData,
  };

  return handleResponse(fetch(fullUrl, options));
}

// =============== Backend Health Check ===============

/**
 * Check if backend is ready by checking the health endpoint
 * @returns Promise<boolean> - true if backend is ready, false otherwise
 */
export async function checkBackendHealth(): Promise<boolean> {
  try {
    const baseURL = await getBaseURL();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1000);

    const res = await fetch(`${baseURL}/health`, {
      signal: controller.signal,
      method: 'GET',
    });

    clearTimeout(timeoutId);
    return res.ok;
  } catch (error) {
    console.log('[Backend Health Check] Not ready:', error);
    return false;
  }
}

// =============== Local Server Stale Detection ===============

/**
 * Git hash of the last commit that touched server/, injected by Vite at build
 * time. When the running server reports a different hash it means the server
 * process is stale and needs to be restarted / rebuilt.
 */
const EXPECTED_SERVER_HASH: string =
  import.meta.env.VITE_SERVER_CODE_HASH || '';

let serverStaleChecked = false;

/**
 * One-time check: when VITE_USE_LOCAL_PROXY is enabled, fetch the local
 * server's /health and compare its server_hash against the expected hash
 * baked into this build. Shows a persistent toast if they differ.
 */
export async function checkLocalServerStale(): Promise<void> {
  if (serverStaleChecked || !EXPECTED_SERVER_HASH) return;
  serverStaleChecked = true;

  const useLocalProxy = import.meta.env.VITE_USE_LOCAL_PROXY === 'true';
  if (!useLocalProxy) return;

  const serverUrl = import.meta.env.VITE_PROXY_URL || 'http://localhost:3001';

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const res = await fetch(`${serverUrl}/health`, {
      signal: controller.signal,
      method: 'GET',
    });

    clearTimeout(timeoutId);

    let staleReason = '';

    if (res.status === 404) {
      // /health endpoint doesn't exist — server predates v0.0.89
      staleReason = 'Server does not have /health endpoint (pre-v0.0.89)';
    } else if (res.ok) {
      const data = await res.json();
      const serverHash: string | undefined = data?.server_hash;

      if (!serverHash) {
        staleReason = 'Server does not report version info (pre-v0.0.89)';
      } else if (
        serverHash !== 'unknown' &&
        serverHash !== EXPECTED_SERVER_HASH
      ) {
        staleReason = `Server hash ${serverHash} != expected ${EXPECTED_SERVER_HASH}`;
      }
    } else {
      // Other HTTP errors — skip
      return;
    }

    if (staleReason) {
      const { toast } = await import('sonner');
      toast.warning(
        i18next.t('layout.server-code-updated', {
          defaultValue: 'Server code has been updated',
        }),
        {
          description: i18next.t('layout.server-outdated-description', {
            defaultValue:
              'The server is outdated. Restart it or rebuild it: docker-compose up --build -d',
          }),
          duration: Infinity,
          closeButton: true,
        }
      );
      console.warn(`[Server Check] ${staleReason}. Please restart the server.`);
    }
  } catch {
    // server not reachable — skip silently
  }
}

/**
 * Simple backend health check with retries
 * @param maxWaitMs - Maximum time to wait in milliseconds (default: 10000ms)
 * @param retryIntervalMs - Interval between retries in milliseconds (default: 500ms)
 * @returns Promise<boolean> - true if backend becomes ready, false if timeout
 */
export async function waitForBackendReady(
  maxWaitMs: number = 10000,
  retryIntervalMs: number = 500
): Promise<boolean> {
  const startTime = Date.now();
  console.log('[Backend Health Check] Waiting for backend to be ready...');

  while (Date.now() - startTime < maxWaitMs) {
    const isReady = await checkBackendHealth();

    if (isReady) {
      console.log(
        `[Backend Health Check] Backend is ready after ${Date.now() - startTime}ms`
      );

      // Fire-and-forget: check local server version when using local proxy
      checkLocalServerStale();

      return true;
    }

    console.log(
      `[Backend Health Check] Backend not ready, retrying... (${Date.now() - startTime}ms elapsed)`
    );
    await new Promise((resolve) => setTimeout(resolve, retryIntervalMs));
  }

  console.error(
    `[Backend Health Check] Backend failed to start within ${maxWaitMs}ms`
  );
  return false;
}
